import {
  updateRuntimeStore,
  useEditorRuntimeStore,
  type RuntimeState,
} from '../stores/useEditorRuntimeStore';
import {
  updateEditorStore,
  type EditorState,
  type ElementMap,
} from '../stores/useEditorStore';
import { type BoundsWithCenter } from '../utils/editor/bounds';
import { type Point } from '../utils/math';

import { type AnimatedTrail } from './animation/animatedTrail';
import { type Config } from './config';
import { sceneCoordsToViewportCoords } from './coords';
import { setCursor } from './cursor';
import { type GenericElement } from './element';
import { type history } from './history';
import { type PathCache } from './render';
import {
  hideNewElementCanvas,
  renderInteractiveScene,
  renderNewElementScene,
  renderStaticScene,
  showNewElementCanvas,
} from './scene';
import { type TransformHandle } from './selection';
import { type GenericShape } from './shapes';

type ActionReferences = ReturnType<typeof getActionReferences>;

export const getActionReferences = () => ({
  renderInteractiveScene,
  renderNewElementScene,
  renderStaticScene,
  setCursor,
  updateEditorStore,
  updateRuntimeStore,
  sceneCoordsToViewportCoords,
  hideNewElementCanvas,
  showNewElementCanvas,
});

export type BaseEventPayload = {
  elementState: EditorState;
  helpers: ActionReferences;
  history: typeof history;
  runtimeState: RuntimeState;
};

export type PointerPayload = BaseEventPayload & {
  event: {
    button: 'both' | 'left' | 'middle' | 'none' | 'right';
    original: PointerEvent;
    pos: { x: number; y: number };
  };
};

export type KeyPayload = BaseEventPayload & {
  event: KeyboardEvent;
};

export type DrawPayload<T> = {
  cache: PathCache;
  config: Config;
  context: CanvasRenderingContext2D;
  element: T;
  elementState: EditorState;
  runtimeState: RuntimeState;
  skipElementUpdate?: boolean;
  isExportImage?: boolean;
};

export type BoundsOptions = { withRotation?: boolean };

export type PluginCursor = string | (() => string | null);

export type Plugin<
  TElement extends GenericElement = GenericElement,
  TActions extends
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, (...args: any[]) => any> | undefined = undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TState extends Record<string, any> | undefined = undefined,
> = {
  collision: {
    bounds: (element: TElement, options?: BoundsOptions) => BoundsWithCenter;
    calculateShape: (element: TElement) => GenericShape;
    isPointInShape: (point: Point, element: TElement) => boolean;
    isPointOnShape: (
      point: Point,
      element: TElement,
      tolerance: number,
    ) => boolean;
    onlyCountDirectElementHitAfterSelection?: boolean;
  };
  cursor: PluginCursor;
  onInit?: () => void;
  events: {
    onPointerDown?: (
      payload: PointerPayload,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    ) => boolean | void | Promise<boolean | void>;
    onPointerMove?: (payload: PointerPayload) => void;
    onPointerUp?: (payload: PointerPayload) => void;
    onKeyDown?: (payload: KeyPayload) => void;
    onKeyUp?: (payload: KeyPayload) => void;
    blockTouchPanning?: boolean;
  };
  export?: (
    element: TElement,
  ) =>
    | Promise<Partial<GenericElement> | undefined>
    | Partial<GenericElement | undefined>;
  import?: (
    element: Partial<TElement>,
  ) => Promise<TElement | undefined> | TElement | undefined;
  name: string;
  render: {
    draw: (payload: DrawPayload<TElement>) => void;
    drawInteractiveOverlay?: (
      context: CanvasRenderingContext2D,
      payload: {
        config: Config;
        elementState: EditorState;
        runtimeState: RuntimeState;
      },
    ) => void;
    padding?: (element: TElement) => number;
    translate?: (
      context: CanvasRenderingContext2D,
      element: TElement,
      scale: number,
    ) => undefined | { x: number; y: number };
    isRetouchTool?: boolean;
  };
  selection?: {
    hideDefaultTransformHandles?:
      | boolean
      | ((state: { elementState: EditorState }) => boolean);
    hideDefaultSelectionBorder?:
      | boolean
      | ((state: { elementState: EditorState }) => boolean);
    getTransformHandles?: (
      element: TElement,
      helpers: { config: Config; state: RuntimeState },
    ) => Record<string, TransformHandle>;
    renderSelectionBorder?: (
      context: CanvasRenderingContext2D,
      element: TElement,
      helpers: { config: Config; state: RuntimeState },
    ) => void;
    renderTransformHandles?: (
      context: CanvasRenderingContext2D,
      handles: Record<string, TransformHandle>,
      helpers: { config: Config; state: RuntimeState },
    ) => void;
    renderHoverBorder?: (
      context: CanvasRenderingContext2D,
      element: TElement,
      helpers: { config: Config; state: RuntimeState },
    ) => void;
    renderTransformHandlesForNewElement?: boolean;
    hasVisiblePointHandles?: boolean;
  };
  trailAnimation?: AnimatedTrail;
  transformation: {
    alwaysKeepAspectRatio?: boolean;
    getCursorFromTransformation?: (type: string) => string;
    onMultiResize?: (
      element: TElement,
      initial: TElement,
      nextWidth: number,
      nextHeight: number,
      transformDirection: string,
    ) => Partial<TElement>;
    onSingleResize?: (
      element: TElement,
      initial: TElement,
      nextWidth: number,
      nextHeight: number,
      transformDirection: string,
    ) => Partial<TElement>;
    resizedBounds: (
      element: TElement,
      newWidth: number,
      newHeight: number,
    ) => BoundsWithCenter;
    resizeTestFromBounds?: (
      element: TElement,
      [cursorX, cursorY]: Point,
      helpers: { config: Config; state: RuntimeState },
    ) => null | string;
    shiftBySizeOnFlip: boolean;
    transformElement?: (
      transform: string,
      element: TElement,
      originalElements: ElementMap,
      [pointerX, pointerY]: Point,
      [originX, originY]: Point,
      helpers: { elementState: EditorState; runtimeState: RuntimeState },
    ) => void;
    manualRotation?: (
      element: TElement,
      originalElement: TElement,
      center: Point,
      centerAngle: number,
    ) => Partial<TElement>;
  };
} & (TActions extends undefined ? unknown : { actions: TActions }) &
  (TState extends undefined ? unknown : { state: TState });

export const getPlugin = (name: string) => {
  const found = useEditorRuntimeStore.getState().plugins.get(name);
  if (!found) throw new Error(`Plugin "${name}" not found`);
  return found;
};

export function resolveBooleanOrFunction<T>(
  value: boolean | ((args: T) => boolean) | undefined,
  args: T,
): boolean {
  if (value === undefined) return false;
  if (typeof value === 'function') return value(args);
  return value;
}
